import { api } from "../../api/apiSlice";

// System Settings (handoff §18) - generic admin-configurable key/value
// store, SYSTEM_ADMIN-only (Settings.ManageIntegrations gates every call
// below, including read). Omitting `group`/`branchId` on the list call
// returns every pre-seeded key at the GLOBAL scope in one response (verified
// live) - no need for four separate round trips. Secrets (isSecret: true)
// are never returned in real form by any endpoint - GET always shows
// "***configured***" (if set) or null (if not); only PUT with a real value
// can change one.
//
// Per-branch overrides (handoff §18.1, added 2026-08-06, first used by
// Vehicle Maintenance's Vehicles.SparesProductGroupId) - every setting is
// global by default (branchId=0, the API's own default when the param is
// omitted). Passing `branchId` on list/set instead reads/writes that
// branch's own override row. Verified live: a branch with no override for a
// given key returns NO row for it at all (not a fallback to the global
// value) - the UI must treat "missing" as "not configured for this branch",
// never silently show the global value in its place.
export const settingsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getSettings: builder.query({
      query: (branchId) => ({ url: "/settings", params: branchId ? { branchId } : undefined }),
      providesTags: (result, error, branchId) => [{ type: "Settings", id: branchId || "GLOBAL" }],
    }),
    // Batch85_CompanyInfoBranchAware.sql - name/address/phone/email for a
    // document letterhead (Stock print, and eventually the Sales quotation
    // PDF too), branch-aware server-side (global, with that branch's own
    // overrides layered on top) and open to any logged-in user - nothing
    // here is confidential, so unlike getSettings above this isn't gated by
    // Settings.ManageIntegrations (that permission's read is admin-only by
    // design; this one deliberately isn't gated by any permission at all).
    getCompanyInfo: builder.query({
      query: (branchId) => ({ url: "/settings/company-info", params: branchId ? { branchId } : undefined }),
      providesTags: (result, error, branchId) => [{ type: "Settings", id: `COMPANY-${branchId || "GLOBAL"}` }],
    }),
    // PUT rejects a blank/empty value outright ("Value cannot be empty.") -
    // there is no way to unset a setting back to blank, only overwrite it
    // with a different non-empty value (verified live).
    setSetting: builder.mutation({
      query: ({ key, value, branchId }) => ({
        url: `/settings/${key}`,
        method: "PUT",
        data: { value },
        params: branchId ? { branchId } : undefined,
      }),
      invalidatesTags: (result, error, { branchId }) => [{ type: "Settings", id: branchId || "GLOBAL" }],
    }),
    testEmailSetting: builder.mutation({
      query: (toAddress) => ({ url: "/settings/test-email", method: "POST", data: { toAddress } }),
    }),
    testWhatsAppSetting: builder.mutation({
      query: (toPhone) => ({ url: "/settings/test-whatsapp", method: "POST", data: { toPhone } }),
    }),
    // Sends a REAL, billed SMS. No branchId: the SMS credentials are global
    // (Batch183) - one provider account for the company - so the server's own
    // branchId=0 default is the right scope here, unlike Email/WhatsApp.
    testSmsSetting: builder.mutation({
      query: (toPhone) => ({ url: "/settings/test-sms", method: "POST", data: { toPhone } }),
    }),
    // Sends a REAL customer service email using the live template and the selected
    // branch letterhead - the way to check Company.* and Technical.Manager* look right
    // before a customer sees them. Systems trades under a different name and shares this
    // template, so it is branch-scoped, unlike the SMS test.
    testServiceEmailSetting: builder.mutation({
      query: ({ toAddress, branchId, sourceType, stage }) => ({
        url: "/settings/test-service-email",
        method: "POST",
        data: { toAddress, branchId: branchId ?? 0, sourceType, stage },
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetSettingsQuery,
  useGetCompanyInfoQuery,
  useLazyGetCompanyInfoQuery,
  useSetSettingMutation,
  useTestEmailSettingMutation,
  useTestWhatsAppSettingMutation,
  useTestSmsSettingMutation,
  useTestServiceEmailSettingMutation,
} = settingsApi;
